import React, { useState, useEffect } from 'react'
import { Button, Select, Space, Typography, theme, Slider, Card, notification } from 'antd'
import {
  SearchOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  VideoCameraOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  MedicineBoxOutlined
} from '@ant-design/icons'
import { readSearchTypes } from '../api/searchApi'
import GoogleAddress from '../../reusable-components/GoogleAddress'
import DischemStoreSelector from './DischemStoreSelector'
import { useSelector } from 'react-redux'
import { geocodeByAddress } from 'react-google-places-autocomplete'

const { Option } = Select
const { Title, Text } = Typography

const SearchRefinement = ({
  onSearch,
  onFilterChange,
  searchFilters = {},
  loading = false,
  searchType,
  setSearchType,
  isVirtualVisit = false,
  isHomeVisit = false,
  preSelectedServiceType = null,
  onBack
}) => {
  const [searchTypes, setSearchTypes] = useState([])
  const [address, setAddress] = useState(searchFilters.address || '')
  const [placeId, setPlaceId] = useState(searchFilters.placeId || '')
  const [location, setLocation] = useState(searchFilters.location || null)
  const [maxDistance, setMaxDistance] = useState(searchFilters.maxDistance || 25)
  const [isLoadingLocation, setIsLoadingLocation] = useState(false)
  const [hasManuallySetLocation, setHasManuallySetLocation] = useState(false)
  const [loadingStartTime, setLoadingStartTime] = useState(null)
  const [isUILoading, setIsUILoading] = useState(false)
  const { token } = theme.useToken()
  const patientAddress = useSelector((state) => state.auth.agiliteUser.residentialAddress)

  // Virtual consultation options
  const serviceTypes = [
    {
      value: 'scheduled',
      label: 'Scheduled Consultation',
      description: 'Book with a specific doctor at a specific time'
    },
    { value: 'flexi', label: 'Flexi Consultation', description: 'A doctor will contact you during the day' },
    { value: 'priority', label: 'Priority Consultation', description: 'A doctor will call you within a few minutes' }
  ]

  const callTypes = [
    { value: 'video', label: 'Video Call' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'whatsapp', label: 'WhatsApp Call' }
  ]

  const appointmentReasons = [
    'General Consultation',
    'Follow-up Appointment',
    'Prescription Renewal',
    'Medical Certificate',
    'Test Results Discussion',
    'Chronic Disease Management',
    'Mental Health Support',
    'Second Opinion',
    'Urgent Medical Advice',
    'Other'
  ]

  // Time of day options
  const timeOfDayOptions = [
    { value: 'any', label: 'Any time' },
    { value: 'morning', label: 'Morning (6:00 - 12:00)' },
    { value: 'afternoon', label: 'Afternoon (12:00 - 18:00)' },
    { value: 'evening', label: 'Evening (18:00 - 22:00)' }
  ]

  useEffect(() => {
    fetchProfessionsSpecialties()
  }, [])

  // Track loading state with minimum animation time
  useEffect(() => {
    if (loading) {
      setLoadingStartTime(Date.now())
      setIsUILoading(true)
    } else if (loadingStartTime) {
      // Ensure minimum loading time of 2 seconds for full animation
      const elapsed = Date.now() - loadingStartTime
      const minimumLoadingTime = 2000 // 2 seconds

      if (elapsed < minimumLoadingTime) {
        const remainingTime = minimumLoadingTime - elapsed
        setTimeout(() => {
          setIsUILoading(false)
        }, remainingTime)
      } else {
        setIsUILoading(false)
      }
    }
  }, [loading, loadingStartTime])

  // Auto-set patient address as default location
  useEffect(() => {
    const updateLocationInRedux = (updates) => {
      Object.entries(updates).forEach(([key, value]) => {
        onFilterChange?.(key, value)
      })
    }

    const setPatientLocation = async () => {
      // Only set if patient has an address, no location is currently set, user hasn't manually set location, and we're not already loading
      if (patientAddress && !location && !hasManuallySetLocation && !isLoadingLocation) {
        setIsLoadingLocation(true)
        try {
          const results = await geocodeByAddress(patientAddress)

          if (results && results.length > 0) {
            const result = results[0]
            const newLocation = {
              address: patientAddress,
              place_id: result.place_id
            }

            setAddress(patientAddress)
            setPlaceId(result.place_id)
            setLocation(newLocation)

            // Save auto-populated location to Redux state
            updateLocationInRedux({
              address: patientAddress,
              placeId: result.place_id,
              location: newLocation
            })
          }
        } catch (error) {
          console.error('Error geocoding patient address:', error)
          // Silently fail - don't show error to user for auto-population
        } finally {
          setIsLoadingLocation(false)
        }
      }
    }

    setPatientLocation()
  }, [patientAddress, location, hasManuallySetLocation, isLoadingLocation, onFilterChange])

  const fetchProfessionsSpecialties = async () => {
    try {
      const tmpSearchTypes = await readSearchTypes()
      setSearchTypes(tmpSearchTypes)
    } catch (error) {}
  }

  // Keep consultType aligned with visit mode
  useEffect(() => {
    if (isVirtualVisit && (!searchFilters.consultType || searchFilters.consultType.length === 0)) {
      onFilterChange?.('consultType', ['virtual'])
      return
    }
    if (!isVirtualVisit && Array.isArray(searchFilters.consultType)) {
      const cleaned = searchFilters.consultType.filter((t) => t !== 'virtual')
      if (cleaned.length !== searchFilters.consultType.length) {
        onFilterChange?.('consultType', cleaned)
      }
    }
  }, [isVirtualVisit, searchFilters.consultType, onFilterChange])

  // Set searchType to virtual_consultation when in Virtual Visit mode, or general_practitioner for clinic visits
  useEffect(() => {
    if (isVirtualVisit && searchType !== 'virtual_consultation') {
      setSearchType('virtual_consultation')
    } else if (!isVirtualVisit && searchType === 'virtual_consultation') {
      // If not virtual visit but searchType is virtual_consultation, reset to general_practitioner
      setSearchType('general_practitioner')
    }
  }, [isVirtualVisit, searchType, setSearchType])

  // Initialize service type for virtual visits
  useEffect(() => {
    if (isVirtualVisit && preSelectedServiceType) {
      // If a service type is pre-selected, use it
      onFilterChange?.('serviceType', preSelectedServiceType)
    } else if (isVirtualVisit && !searchFilters.serviceType) {
      onFilterChange?.('serviceType', 'scheduled') // Default to scheduled
    }
  }, [isVirtualVisit, preSelectedServiceType, searchFilters.serviceType, onFilterChange])

  // Ensure contact method and reason start empty for virtual visits to show placeholders
  // Removed auto-reset of fields to preserve user selections across navigation

  const handleNewSearch = () => {
    // For virtual visits, validate required fields first
    if (isVirtualVisit) {
      if (!searchFilters.callType || !searchFilters.appointmentReason || !searchFilters.timeOfDay) {
        notification.warning({
          message: 'Required Fields Missing',
          description:
            'Please complete all fields (contact method, reason for visit, and time of day) before searching.',
          duration: 3
        })
        return
      }
    }

    // For flexi/priority consultations, skip doctor search and go directly to booking
    const isFlexiPriority =
      isVirtualVisit && (searchFilters.serviceType === 'flexi' || searchFilters.serviceType === 'priority')

    if (isFlexiPriority) {
      // Navigate directly to booking confirmation for flexi/priority
      const flexiPriorityParams = {
        ...searchFilters,
        searchType,
        isFlexiPriority: true
      }

      console.log('🚀 Desktop Flexi/Priority - Skipping doctor search:', flexiPriorityParams)
      onSearch?.(flexiPriorityParams)
      return
    }

    const searchParams = {
      ...searchFilters,
      searchType,
      address,
      placeId,
      location,
      maxDistance
    }

    console.log('🔍 Desktop Search - Performing new search with filters:', searchParams)
    onSearch?.(searchParams)
  }

  const handleSearchTypeChange = (value) => {
    console.log('SearchType changed to:', value)
    console.log('Current searchType prop:', searchType)

    // Update Redux state first
    onFilterChange?.('searchType', value)

    // Automatically search again with the new search type
    // Make sure to use the new value and override any old searchType in searchFilters
    const searchParams = {
      ...searchFilters,
      searchType: value,
      address,
      placeId,
      location,
      maxDistance
    }

    console.log('Searching with params:', searchParams)
    onSearch?.(searchParams)
  }

  const handleLocationChange = (value) => {
    setHasManuallySetLocation(true) // Mark as manually set by user
    if (value && value.value) {
      const newPlaceId = value.value.place_id
      const newAddress = value.label
      const newLocation = {
        address: newAddress,
        place_id: newPlaceId
      }

      setPlaceId(newPlaceId)
      setAddress(newAddress)
      setLocation(newLocation)

      // Save to Redux state
      onFilterChange?.('placeId', newPlaceId)
      onFilterChange?.('address', newAddress)
      onFilterChange?.('location', newLocation)
    } else {
      setPlaceId('')
      setAddress('')
      setLocation(null)

      // Save to Redux state
      onFilterChange?.('placeId', '')
      onFilterChange?.('address', '')
      onFilterChange?.('location', null)
    }
  }

  // Synchronize local state with Redux state when it changes
  useEffect(() => {
    if (searchFilters.address !== address) setAddress(searchFilters.address || '')
    if (searchFilters.placeId !== placeId) setPlaceId(searchFilters.placeId || '')
    if (JSON.stringify(searchFilters.location) !== JSON.stringify(location)) {
      setLocation(searchFilters.location || null)
    }
    if (searchFilters.maxDistance !== maxDistance) setMaxDistance(searchFilters.maxDistance || 25)
  }, [
    searchFilters.address,
    searchFilters.placeId,
    searchFilters.location,
    searchFilters.maxDistance,
    address,
    placeId,
    location,
    maxDistance
  ])

  return (
    <Card
      style={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '20px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
        maxWidth: '420px',
        overflow: 'hidden'
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ padding: '32px' }}>
        {/* Back Button for Virtual Visits */}
        {isVirtualVisit && onBack && (
          <div style={{ marginBottom: '16px' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#6b7280',
                padding: '4px 8px',
                height: 'auto'
              }}
            >
              Back to Consultation Types
            </Button>
          </div>
        )}

        {/* Header */}
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <Title
            level={3}
            style={{
              color: '#1f2937',
              marginBottom: '8px',
              fontSize: '24px',
              fontWeight: '700'
            }}
          >
            Find Your Doctor
          </Title>
          <Text style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.5' }}>
            Search for healthcare professionals near you
          </Text>
        </div>

        {/* Specialty Selector - Only show for non-virtual visits */}
        {!isVirtualVisit && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
              <Space>
                <span style={{ color: token.colorPrimary }}>🩺</span>
                Medical Specialty
              </Space>
            </Text>
            <Select
              showSearch
              allowClear
              style={{ width: '100%' }}
              placeholder='Select medical specialty...'
              value={searchType}
              onChange={handleSearchTypeChange}
              optionLabelProp='label'
              size='large'
              disabled={isUILoading}
              filterOption={(input, option) =>
                option.children.props.children[1].props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {searchTypes.map((option) => (
                <Option key={option.value} value={option.value} label={option.label}>
                  <Space>
                    <span style={{ fontSize: '16px' }}>{option.icon}</span>
                    <div>
                      <div style={{ fontWeight: '500' }}>{option.label}</div>
                      <Text type='secondary' style={{ fontSize: '12px' }}>
                        {option.category}
                      </Text>
                    </div>
                  </Space>
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Service Type Selection - COMPLETELY HIDDEN FOR CLINIC BOOKINGS */}
        {false && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
              <Space>
                <ClockCircleOutlined style={{ color: token.colorPrimary }} />
                Service Type
              </Space>
            </Text>
            <Select
              placeholder='Choose service type...'
              value={searchFilters.serviceType}
              onChange={(value) => onFilterChange?.('serviceType', value)}
              style={{ width: '100%' }}
              size='large'
              disabled={isUILoading}
            >
              {serviceTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  <div style={{ padding: '4px 0' }}>
                    <div style={{ fontWeight: '500', marginBottom: '2px' }}>{type.label}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{type.description}</div>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Contact Method Selection - Only for Virtual Visits */}
        {isVirtualVisit && searchFilters.serviceType && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
              <Space>
                <VideoCameraOutlined style={{ color: token.colorPrimary }} />
                Contact Method
              </Space>
            </Text>
            <Select
              placeholder='- Select Contact Method -'
              value={searchFilters.callType}
              onChange={(value) => onFilterChange?.('callType', value)}
              style={{ width: '100%' }}
              size='large'
              disabled={isUILoading}
            >
              <Option value={null}>- Select Contact Method -</Option>
              {callTypes.map((type) => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Reason for Visit Selection - Only for Virtual Visits */}
        {isVirtualVisit && searchFilters.serviceType && searchFilters.callType && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
              <Space>
                <CalendarOutlined style={{ color: token.colorPrimary }} />
                Reason for Visit
              </Space>
            </Text>
            <Select
              placeholder='Choose the reason for your visit...'
              value={searchFilters.appointmentReason}
              onChange={(value) => onFilterChange?.('appointmentReason', value)}
              style={{ width: '100%' }}
              size='large'
              disabled={isUILoading}
            >
              {appointmentReasons.map((reason) => (
                <Option key={reason} value={reason}>
                  {reason}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Time of Day Selection - Only for Virtual Visits */}
        {isVirtualVisit && searchFilters.serviceType && searchFilters.callType && searchFilters.appointmentReason && (
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
              <Space>
                <ClockCircleOutlined style={{ color: token.colorPrimary }} />
                Preferred Time of Day
              </Space>
            </Text>
            <Select
              placeholder='Select preferred time of day...'
              value={searchFilters.timeOfDay}
              onChange={(value) => onFilterChange?.('timeOfDay', value)}
              style={{ width: '100%' }}
              size='large'
              disabled={isUILoading}
            >
              {timeOfDayOptions.map((time) => (
                <Option key={time.value} value={time.value}>
                  {time.label}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Prescription Handling - Only for Virtual Visits */}
        {isVirtualVisit &&
          searchFilters.serviceType &&
          searchFilters.callType &&
          searchFilters.appointmentReason &&
          searchFilters.timeOfDay && (
            <div style={{ marginBottom: '24px' }}>
              <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
                <Space>
                  <MedicineBoxOutlined style={{ color: token.colorPrimary }} />
                  Prescription Handling
                </Space>
              </Text>
              <Select
                placeholder='How would you like to handle prescriptions?'
                value={
                  searchFilters.dischem?.required
                    ? searchFilters.dischem.reqType === 'C'
                      ? 'collection'
                      : 'delivery'
                    : 'self'
                }
                onChange={(value) => {
                  let dischemData = { required: false, reqType: null, store: null }
                  if (value === 'collection') {
                    dischemData = { required: true, reqType: 'C', store: searchFilters.dischem?.store || null }
                  } else if (value === 'delivery') {
                    dischemData = { required: true, reqType: 'D', store: searchFilters.dischem?.store || null }
                  }
                  onFilterChange?.('dischem', dischemData)
                }}
                style={{ width: '100%' }}
                size='large'
                disabled={isUILoading}
              >
                <Option value='self'>I'll handle prescriptions myself</Option>
                <Option value='collection'>Dis-Chem Collection</Option>
                <Option value='delivery'>Dis-Chem Delivery</Option>
              </Select>

              {/* Dis-Chem Store Selection - Show when collection or delivery is selected */}
              {searchFilters.dischem?.required && (
                <div style={{ marginTop: '16px' }}>
                  <Text strong style={{ display: 'block', marginBottom: '8px', color: '#374151', fontSize: '14px' }}>
                    Select Dis-Chem Store
                  </Text>
                  <DischemStoreSelector
                    value={searchFilters.dischem?.store}
                    onChange={(store) => {
                      const updatedDischem = {
                        ...searchFilters.dischem,
                        store: store
                      }
                      onFilterChange?.('dischem', updatedDischem)
                    }}
                    disabled={isUILoading}
                  />
                </div>
              )}
            </div>
          )}

        {/* Location Input - Only show for non-virtual visits (clinic bookings) */}
        {!isVirtualVisit && (
          <div style={{ marginBottom: '24px' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}
            >
              <Text strong style={{ color: '#374151', fontSize: '16px' }}>
                <Space>
                  <EnvironmentOutlined style={{ color: token.colorPrimary }} />
                  Location
                </Space>
              </Text>
              {(searchFilters.address || location) && (
                <Button
                  type='text'
                  size='small'
                  icon={<CloseOutlined />}
                  onClick={() => {
                    // Clear local state
                    setAddress('')
                    setPlaceId('')
                    setLocation(null)
                    setMaxDistance(25)
                    setIsLoadingLocation(false)
                    setHasManuallySetLocation(false) // Allow auto-population again

                    // Clear Redux state
                    onFilterChange?.('address', '')
                    onFilterChange?.('placeId', '')
                    onFilterChange?.('location', null)
                    onFilterChange?.('maxDistance', 25)
                  }}
                  style={{
                    color: '#6b7280',
                    fontSize: '12px',
                    height: '24px',
                    padding: '0 8px',
                    borderRadius: '6px'
                  }}
                  title='Clear location'
                >
                  Clear
                </Button>
              )}
            </div>
            <GoogleAddress
              id='search-location'
              selectProps={{
                placeholder: isLoadingLocation
                  ? 'Loading your address...'
                  : searchFilters.address || location?.address || 'Search for an address, suburb, or city',
                value: searchFilters.address || location?.address || undefined,
                onChange: handleLocationChange,
                size: 'large',
                loading: isLoadingLocation,
                style: {
                  borderRadius: '8px',
                  borderColor: '#d1d5db'
                }
              }}
            />
            {(searchFilters.address === patientAddress || (location && location.address === patientAddress)) && (
              <Text
                type='secondary'
                style={{
                  fontSize: '12px',
                  marginTop: '4px',
                  display: 'block',
                  color: token.colorSuccess
                }}
              >
                ✓ Using your profile address
              </Text>
            )}
          </div>
        )}

        {/* Distance Selector - Always visible when location is selected and not virtual consultation */}
        {location && searchType !== 'virtual_consultation' && (
          <div style={{ marginBottom: '32px' }}>
            <Text strong style={{ color: '#374151', marginBottom: '16px', display: 'block', fontSize: '16px' }}>
              <Space>
                <span style={{ color: token.colorPrimary }}>📍</span>
                Search Radius
              </Space>
            </Text>

            <div style={{ padding: '0 16px' }}>
              <Slider
                min={5}
                max={100}
                step={5}
                value={maxDistance}
                onChange={(value) => {
                  setMaxDistance(value)
                  onFilterChange?.('maxDistance', value)
                }}
                marks={{
                  5: '5km',
                  25: '25km',
                  50: '50km',
                  100: '100km'
                }}
                tooltip={{
                  formatter: (value) => `${value}km radius`
                }}
                trackStyle={{ backgroundColor: token.colorPrimary, height: '6px' }}
                handleStyle={{ borderColor: token.colorPrimary, borderWidth: '3px', width: '20px', height: '20px' }}
                railStyle={{ height: '6px', backgroundColor: '#f0f0f0' }}
              />
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Text style={{ fontSize: '14px', color: '#6b7280' }}>
                  Search within <strong style={{ color: token.colorPrimary }}>{maxDistance}km</strong> radius
                </Text>
              </div>
            </div>
          </div>
        )}

        {/* Search Button */}
        <Button
          type='primary'
          icon={<SearchOutlined />}
          onClick={handleNewSearch}
          loading={isUILoading}
          block
          size='large'
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            borderColor: 'transparent',
            fontWeight: '600',
            height: '52px',
            borderRadius: '12px',
            fontSize: '16px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-1px)'
            e.target.style.boxShadow = '0 6px 25px rgba(59, 130, 246, 0.4)'
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)'
            e.target.style.boxShadow = '0 4px 20px rgba(59, 130, 246, 0.3)'
          }}
        >
          {isVirtualVisit && (searchFilters.serviceType === 'flexi' || searchFilters.serviceType === 'priority')
            ? 'Continue to Payment'
            : location && searchType !== 'virtual_consultation'
            ? 'Find Nearby Doctors'
            : 'Find Doctors'}
        </Button>

        {(searchFilters.address || location) && searchType !== 'virtual_consultation' && (
          <div
            style={{
              marginBottom: '16px',
              textAlign: 'center',
              padding: '12px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}
          >
            <Text style={{ fontSize: '14px', color: '#475569' }}>
              <EnvironmentOutlined style={{ color: token.colorPrimary, marginRight: '6px' }} />
              Searching within <strong>{maxDistance}km</strong> of
            </Text>
            <br />
            <Text style={{ fontSize: '13px', color: '#64748b' }}>{searchFilters.address || location.address}</Text>
          </div>
        )}
      </div>
    </Card>
  )
}

export default SearchRefinement
